/**
 * La foto de fondo de una caja (sección, tarjeta, contenedor), como IMAGEN de
 * verdad pegada detrás del contenido —«fondo anclado»— y no como
 * `background-image` de CSS.
 *
 * Una imagen de CSS no la cambia la IA (no tiene ranura), no tiene versiones
 * chicas para el teléfono, ni texto alternativo, ni recorte, ni velo. Los
 * templates por eso ponían a mano una imagen en posición absoluta que llena la
 * caja; esto hace lo mismo desde Fondo › Imagen, sin saber de capas:
 *
 *   - la imagen lleva `backgroundLayer: true` y el dibujo la coloca sola —toda
 *     la caja, detrás del contenido, sin recibir clics— (`backgroundLayerRecord`);
 *   - la caja se convierte en su contexto de apilado (`backgroundHostRecord`)
 *     para que «detrás» sea detrás de SU contenido y no de la página.
 *
 * Las que los templates ya pusieron a mano se reconocen igual
 * (`isManualBackdrop`) para editarlas desde Fondo sin cambiarlas.
 */

type LayerNode = {
  id: string
  type: string
  backgroundLayer?: boolean | undefined
  isDecorative?: boolean | undefined
  cmsRepeat?: unknown
  styles: { desktop: Record<string, string | undefined> }
  children: LayerNode[]
}

/** Una imagen que un template ya pegó de fondo: absoluta y del alto de su caja. */
export function isManualBackdrop(node: LayerNode): boolean {
  if (node.type !== 'image') return false
  const d = node.styles?.desktop ?? {}
  return d.position === 'absolute' && d.height === '100%' && (!d.width || d.width === '100%')
}

/** La foto de fondo de una caja, si tiene: la marcada o la puesta a mano. */
export function findBackgroundLayer<T extends LayerNode>(host: T): T | null {
  const kids = (host.children ?? []) as T[]
  return kids.find((child) => child.type === 'image' && child.backgroundLayer) ?? kids.find((child) => isManualBackdrop(child)) ?? null
}

/** The latest gallery photo repeats inside a decorative wrapper behind the
 * heading. Its veil must set contrast on the section, outside that repeat. */
export function findBackgroundPhotoLayer<T extends LayerNode>(host: T): T | null {
  const direct = findBackgroundLayer(host)
  if (direct) return direct
  for (const child of (host.children ?? []) as T[]) {
    const styles = child.styles?.desktop ?? {}
    if (!child.isDecorative || !child.cmsRepeat || styles.position !== 'absolute'
      || styles.width !== '100%' || styles.height !== '100%') continue
    const photo = (child.children as T[]).find((node) => node.type === 'image' && node.isDecorative)
    if (photo) return photo
  }
  return null
}

/** Lo que fija el dibujo en una imagen de fondo, en todos los tamaños. */
export function backgroundLayerRecord(record: Record<string, string>): Record<string, string> {
  return {
    ...record,
    position: 'absolute',
    top: '0',
    left: '0',
    right: 'auto',
    bottom: 'auto',
    width: '100%',
    height: '100%',
    minHeight: '0px',
    maxWidth: 'none',
    maxHeight: 'none',
    /* Llena su caja: una proporción propia la deformaba (alto 100 % y ancho
       100 % con `aspect-ratio` se contradicen). */
    aspectRatio: 'auto',
    margin: '0',
    zIndex: '-1',
    objectFit: record.objectFit || 'cover',
    borderRadius: 'inherit',
    pointerEvents: 'none',
  }
}

/** La caja con foto de fondo: posicionada y con su propio apilado. */
export function backgroundHostRecord(record: Record<string, string>): Record<string, string> {
  return {
    ...record,
    position: record.position && record.position !== 'static' ? record.position : 'relative',
    isolation: 'isolate',
  }
}

/** Si una imagen es la foto de fondo de su caja (anclada o pegada a mano). */
export function isBackgroundImage(node: LayerNode): boolean {
  return node.type === 'image' && (Boolean(node.backgroundLayer) || isManualBackdrop(node))
}

export function hasBackgroundLayer(node: { children?: { backgroundLayer?: boolean | undefined }[] | undefined }): boolean {
  return Boolean(node.children?.some((child) => child.backgroundLayer))
}

/**
 * Qué tono deja el velo de la foto de fondo, para el color del texto encima.
 *
 * El contraste automático mira el color de fondo de la caja; con una foto de
 * fondo ese color queda debajo y no decide nada. Sin poder mirar la foto, el
 * velo sí lo dice: uno oscuro (sombra, parejo, bordes, o un tinte o degradado
 * de color oscuro) con fuerza pide texto blanco; un tinte claro fuerte, texto
 * oscuro. Sin velo —o uno flojo— devuelve `null`: no se sabe, manda la caja.
 */
export function backgroundPhotoTone(
  veil: { kind: string; strength: number; strengthTo?: number | undefined; color?: string | undefined; colorTo?: string | undefined } | undefined,
  isDark: (color: string) => boolean | null,
  /** La opacidad de la foto (0–1): con la foto casi transparente, el velo tampoco se ve y manda la caja. */
  opacity = 1,
): 'dark' | 'light' | null {
  if (!veil) return null
  const visible = Number.isFinite(opacity) ? Math.max(0, Math.min(1, opacity)) : 1
  const strength = Math.max(veil.strength ?? 0, veil.kind === 'gradient' ? veil.strengthTo ?? 0 : 0) * visible
  if (strength < 35) return null
  if (veil.kind === 'bottom' || veil.kind === 'even' || veil.kind === 'edges') return 'dark'
  const colors = veil.kind === 'tint' ? [veil.color || 'var(--wb-accent)'] : [veil.color || 'var(--wb-primary)', ...(veil.colorTo ? [veil.colorTo] : [])]
  const tones = colors.map(isDark)
  if (tones.every((tone) => tone === true)) return 'dark'
  if (tones.every((tone) => tone === false) && strength >= 50) return 'light'
  return null
}
